import { useEffect, useState, FC } from 'react';
import {
  NextgenImage,
  Theme,
  Button,
  ButtonStyles,
  ButtonWidth,
  useTranslation,
  useNextgenRouter,
  useSession,
  LoaderType,
  type ButtonTheme,
} from '@nextgen-web-framework/all';
import { useSelector, useDispatch } from 'react-redux';
import type { AppState, AppDispatch } from '@nextgen-web-framework/core';
import { RoutePaths, AccountLifeCycleStatusType, RenewalCardCallbackType } from '@commerce-profile-webcomponents/sdk';
import { fetchAccountLifeCycleCMSIfUndefined } from '../../../utils/ProfileCMSUtils';
import { accountRenewalBannerStatus, initialAccountLifeCycleStatus } from '../../../utils/accountLifeCycleUtils';
import { useLocale } from '../../../utils/customHooks';
import { parseObject, siteCountry, ifThenElse } from '../../../utils/helper';
import { useMyAccountProfileConfig } from '../../../utils/masterdata';
import { useGlobalErrorHandler } from '../../../utils/globalErrorHook';
import { useFeatureFlag } from '../../../utils/featureFlagHook';
import type { RenewalCardProps } from './types/propTypes';
import styles from './RenewalCard.module.scss';

const RenewalCard: FC<RenewalCardProps> = ({ callback, ctaCallback }) => {
  const t = useTranslation();
  const router = useNextgenRouter();
  const dispatch = useDispatch<AppDispatch>();
  const { session } = useSession();
  const { type: accountType } = session;
  const siteLocale = useLocale();
  const siteCountryCode = siteCountry().toUpperCase();

  // Get masterdata
  const myAccountConfig = useMyAccountProfileConfig();
  const apiErrorHandler = useGlobalErrorHandler();

  // Feature flags
  const featureFlags = useFeatureFlag();

  const [accountRenewalStatus, setAccountRenewalStatus] =
    useState<AccountLifeCycleStatusType>(initialAccountLifeCycleStatus);

  const {
    mustRenew,
    inactiveDate,
    expiryDate,
    rawExpiryDate,
    // isAutoRenewalEnabled,
    // isAutoRenewalFailed,
    showRenewalNudge,
    isLastGraceMonth,
  } = parseObject(accountRenewalStatus);

  useEffect(() => {
    accountRenewalBannerStatus(
      session,
      myAccountConfig,
      featureFlags,
      siteLocale,
      setAccountRenewalStatus,
      undefined,
      apiErrorHandler,
    );
  }, [session, siteLocale]);

  useEffect(() => {
    if (callback && rawExpiryDate && expiryDate) {
      const callbackData: RenewalCardCallbackType = {
        expiryDate: rawExpiryDate,
        formattedExpiryDate: expiryDate,
        isUrgent: isLastGraceMonth,
      };
      callback(callbackData);
    }
  }, [rawExpiryDate, expiryDate, isLastGraceMonth]);

  const accountLifeCycleCmsData = useSelector((state: AppState) => state.commerceProfile.accountLifeCycleCmsData);
  const { renewalCardImage } = accountLifeCycleCmsData;

  useEffect(() => {
    if (showRenewalNudge) {
      // skip to call api if not needed
      fetchAccountLifeCycleCMSIfUndefined(dispatch, accountLifeCycleCmsData, siteLocale, apiErrorHandler);
    }
  }, [siteLocale, showRenewalNudge]);

  const handleButtonClick = () => {
    if (ctaCallback) {
      ctaCallback();
    } else {
      router.push(`${RoutePaths.ACCOUNT_INFORMATION}?isRenewal=true`);
    }
  };

  const lastGracePeriodLabel = ifThenElse(isLastGraceMonth, '.lastMonth', '');

  const cardConfig = {
    default: {
      cardTitle: <>{t(`profile.AccountRenewal.RenewalNudge.defaultExpiryTitle.${siteCountryCode}.${accountType}`)}</>,
      cardMessage: t(`profile.AccountRenewal.RenewalNudge.defaultExpiryMessage.${siteCountryCode}`, {
        date: expiryDate,
      }),
      buttonText: 'profile.AccountRenewal.ManualRenewal.button',
      buttonTheme: Theme.PURPLE,
      buttonBgColor: '',
      style: styles['purple'],
    },
    mustRenew: {
      cardTitle: <>{t(`profile.AccountRenewal.RenewalNudge.expiredTitle.${siteCountryCode}.${accountType}`)}</>,
      cardMessage: t(
        `profile.AccountRenewal.RenewalNudge.expiredMessage.${siteCountryCode}.${accountType}${lastGracePeriodLabel}`,
        { date: inactiveDate },
      ),
      buttonText: 'profile.AccountRenewal.ManualRenewal.button',
      buttonTheme: ifThenElse(isLastGraceMonth, Theme.RED, Theme.ORANGE),
      buttonBgColor: ifThenElse(isLastGraceMonth, '#D91734', ''),
      style: ifThenElse(isLastGraceMonth, styles['red'], styles['orange']),
    },
    autoRenewalEnabled: {
      cardTitle: <>{t('profile.AccountRenewal.RenewalNudge.autoRenewalEnabledTitle')}</>,
      cardMessage: t('profile.AccountRenewal.RenewalNudge.autoRenewalEnabledMessage'),
      buttonText: '',
      buttonTheme: Theme.PURPLE,
      buttonBgColor: '',
      style: styles['purple'],
    },
    autoRenewalFailed: {
      cardTitle: <>{t('profile.AccountRenewal.RenewalNudge.autoRenewalFailedTitle')}</>,
      cardMessage: t('profile.AccountRenewal.RenewalNudge.autoRenewalFailedMessage'),
      buttonText: 'profile.AccountRenewal.ManualRenewal.button',
      buttonTheme: Theme.ORANGE,
      buttonBgColor: '',
      style: styles['orange'],
    },
  };

  // const nonAutoRenewalConfig = mustRenew ? cardConfig.mustRenew : cardConfig.default;
  // const autoRenewalConfig = isAutoRenewalFailed ? cardConfig.autoRenewalFailed : nonAutoRenewalConfig;
  // const config = isAutoRenewalEnabled ? autoRenewalConfig : nonAutoRenewalConfig;

  const config = mustRenew ? cardConfig.mustRenew : cardConfig.default;

  const { cardTitle, cardMessage, buttonText, buttonTheme, style, buttonBgColor } = config;

  return (
    <>
      {showRenewalNudge && (
        <div className={`${styles['card-container']} ${style}`} data-testid="renewal-card-testid">
          <div className={styles['left']}>
            <div className={styles['title']}>{cardTitle}</div>
            <div className={styles['content']}>{cardMessage}</div>

            {buttonText && (
              <div className={styles['button']}>
                <Button
                  title={t(buttonText)}
                  buttonStyle={ButtonStyles.SOLID}
                  buttonWidth={ButtonWidth.FULL_WIDTH}
                  theme={buttonTheme as ButtonTheme}
                  onClick={handleButtonClick}
                  data-testid="btn-testid"
                  backgroundColor={buttonBgColor}
                />
              </div>
            )}
          </div>
          <div className={styles['right-image']}>
            {renewalCardImage && (
              <div className={styles['image-container']}>
                <NextgenImage
                  src={renewalCardImage?.url}
                  loaderType={LoaderType.CONTENTSTACK}
                  alt={renewalCardImage?.altText}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default RenewalCard;
